import React, { useMemo, useState } from "react";
import api from "../services/api";
import { KILL_CHAIN, STAGE_OF, activeTab, useConsole } from "../lib/console";
import { usePoll } from "../lib/hooks";
import { SEVERITIES, attackLabel, attackVar, fmtDateTime, num, plural, sevLabel, sevTone, sevVar, timeAgo } from "../lib/format";
import { Badge, Button, Callout, Card, Empty, ErrorState, Meter, Segmented, SevBadge, SkeletonRows, Stat, cx, useToast } from "../components/ui";
import { ActivityChart, BarList } from "../components/charts";
import { PageHeader, PageTabs } from "../components/ConsoleShell";

// ── Trends ──────────────────────────────────────────────────────
function PatternWatch() {
  const { predictions } = useConsole();
  return (
    <Card title="Pattern watch" subtitle="Rule-based escalation patterns in the last 100 detections" icon="trendUp">
      {!predictions ? (
        <SkeletonRows rows={4} />
      ) : (
        <div className="col" style={{ gap: 14 }}>
          {predictions.map((p) => (
            <div key={p.type} className="card-inset" style={{ padding: 12 }}>
              <div className="row-between">
                <strong style={{ fontSize: 13.5 }}>{p.type}</strong>
                {p.severity === "INFO" ? <Badge tone="ok">Stable</Badge> : <SevBadge sev={p.severity} />}
              </div>
              <p className="small dim" style={{ margin: "6px 0 10px" }}>
                {p.message}
              </p>
              <div className="row-between tiny muted" style={{ marginBottom: 6 }}>
                <span>Signal strength · {p.timeframe}</span>
                <span className="tnum strong" style={{ color: "var(--text)" }}>
                  {p.probability}%
                </span>
              </div>
              <Meter value={p.probability} tone={p.severity === "INFO" ? "ok" : sevTone(p.severity)} label={`${p.type} signal strength`} />
            </div>
          ))}
          <p className="tiny muted">The percentage scales with how many matching detections were seen. It is a heuristic, not a statistical forecast.</p>
        </div>
      )}
    </Card>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Heatmap() {
  const [days, setDays] = useState(7);
  const { data, error, reload } = usePoll("/api/analytics/heatmap", { params: { days }, interval: 60000 });
  const [hover, setHover] = useState(null);
  const grid = data?.heatmap || {};
  const max = Math.max(1, ...DAYS.flatMap((d) => Object.values(grid[d] || {})));
  const total = DAYS.reduce((s, d) => s + Object.values(grid[d] || {}).reduce((a, b) => a + b, 0), 0);
  const shade = (v) => (v ? `color-mix(in srgb, var(--brand) ${Math.round(18 + (v / max) * 82)}%, var(--panel-3))` : "var(--panel-3)");

  return (
    <Card
      title="When attacks happen"
      subtitle={`Detections by weekday and hour (UTC) · ${num(total)} in the last ${days} days`}
      icon="calendar"
      actions={
        <Segmented
          size="sm"
          label="Heatmap window"
          value={days}
          onChange={setDays}
          options={[
            { id: 7, label: "7 days" },
            { id: 30, label: "30 days" },
          ]}
        />
      }
    >
      {error ? (
        <ErrorState detail={error} onRetry={reload} />
      ) : !data ? (
        <SkeletonRows rows={7} h={18} />
      ) : (
        <>
          <div className="row-between" style={{ marginBottom: 10, minHeight: 22 }}>
            <span className="small" aria-live="polite">
              {hover ? (
                <>
                  <strong>
                    {hover.d} {String(hover.h).padStart(2, "0")}:00 UTC
                  </strong>{" "}
                  · {plural(hover.v, "detection")}
                </>
              ) : (
                <span className="muted">Hover a cell for its count</span>
              )}
            </span>
            <span className="heat-scale">
              Fewer
              {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                <i key={f} style={{ background: shade(f * max) }} />
              ))}
              More
            </span>
          </div>
          <div className="heat" role="grid" aria-label="Detections heatmap by weekday and UTC hour">
            <span />
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h} className="heat-axis">
                {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
              </span>
            ))}
            {DAYS.map((d) => (
              <React.Fragment key={d}>
                <span className="heat-label">{d}</span>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = grid[d]?.[h] ?? grid[d]?.[String(h)] ?? 0;
                  return (
                    <span
                      key={h}
                      className="heat-cell"
                      role="gridcell"
                      aria-label={`${d} ${h}:00 UTC, ${v} detections`}
                      style={{ background: shade(v) }}
                      onMouseEnter={() => setHover({ d, h, v })}
                      onMouseLeave={() => setHover(null)}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function Trends() {
  const c = useConsole();
  const [metric, setMetric] = useState("threats");
  const types = usePoll("/api/attack-types", { interval: 30000 });

  const sev = useMemo(
    () =>
      c.stats
        ? SEVERITIES.map((s) => ({ key: s, label: sevLabel(s), value: c.stats.severity_breakdown?.[s] || 0, color: sevVar(s) })).filter((x) => x.value > 0)
        : null,
    [c.stats],
  );
  const countries = useMemo(
    () => (c.stats ? Object.entries(c.stats.top_countries || {}).map(([k, v]) => ({ key: k, label: k, value: v, color: "var(--brand)" })) : null),
    [c.stats],
  );
  const day = useMemo(
    () => (types.data ? (types.data.items || []).map((i) => ({ key: i.type, label: attackLabel(i.type), value: i.count, color: attackVar(i.type) })) : null),
    [types.data],
  );

  return (
    <>
      <div className="grid cols-12">
        <Card
          className="span-8"
          title="Activity"
          subtitle="Last 10 minutes in 10-second buckets"
          icon="activity"
          actions={
            <Segmented
              size="sm"
              label="Activity metric"
              value={metric}
              onChange={setMetric}
              options={[
                { id: "threats", label: "Detections" },
                { id: "events", label: "Events analysed" },
              ]}
            />
          }
        >
          <ActivityChart timeline={c.timeline} metric={metric} height={300} />
        </Card>
        <div className="span-4">
          <PatternWatch />
        </div>
      </div>
      <div className="grid cols-12" style={{ marginTop: 16 }}>
        <Card className="span-4" title="Attack types, last 24 h" subtitle="From the detection database · select to hunt" icon="layers">
          {types.error ? <ErrorState detail={types.error} onRetry={types.reload} /> : <BarList items={day} onSelect={(it) => c.hunt({ attack_type: it.key })} empty="No detections in the last 24 hours." />}
        </Card>
        <Card className="span-4" title="Severity" subtitle="Detections since the backend started" icon="alert">
          <BarList items={sev} empty="No detections yet." />
        </Card>
        <Card className="span-4" title="Origin countries" subtitle="Top 10 since the backend started · select to hunt" icon="globe">
          <BarList items={countries} max={10} empty="No located detections yet." onSelect={(it) => c.hunt({ country: it.key })} />
        </Card>
      </div>
      <div style={{ marginTop: 16 }}>
        <Heatmap />
      </div>
    </>
  );
}

// ── Attack surface ──────────────────────────────────────────────
function KillChainCoverage() {
  const { data, error, reload } = usePoll("/api/attack-types", { interval: 30000 });
  const stages = useMemo(() => {
    const m = Object.fromEntries(KILL_CHAIN.map((s) => [s, { total: 0, types: [] }]));
    (data?.items || []).forEach((i) => {
      const s = STAGE_OF[i.type];
      if (!s) return;
      m[s].total += i.count;
      m[s].types.push(i);
    });
    return m;
  }, [data]);
  const reached = KILL_CHAIN.filter((s) => stages[s].total > 0).length;
  const deep = ["Command & Control", "Exfiltration", "Impact"].some((s) => stages[s].total > 0);

  return (
    <Card title="Kill-chain coverage" subtitle={data ? `${reached} of 8 stages observed in the last 24 hours` : "Last 24 hours"} icon="route">
      {error ? (
        <ErrorState detail={error} onRetry={reload} />
      ) : !data ? (
        <SkeletonRows rows={3} h={40} />
      ) : (
        <>
          <div className="chain">
            {KILL_CHAIN.map((s, i) => {
              const st = stages[s];
              return (
                <div key={s} className={cx("chain-step", st.total > 0 && "is-hit", st.total > 0 && i >= 5 && "is-crit")} title={st.types.map((t) => `${attackLabel(t.type)}: ${t.count}`).join("\n") || "Not observed"}>
                  <div className="chain-step-n">{String(i + 1).padStart(2, "0")}</div>
                  <div className="chain-step-name">{s}</div>
                  <div className="chain-step-val">{num(st.total)}</div>
                  <div className="tiny muted" style={{ marginTop: 2 }}>
                    {st.types.length ? st.types.map((t) => attackLabel(t.type)).join(", ") : "Not observed"}
                  </div>
                </div>
              );
            })}
          </div>
          {deep && (
            <Callout tone="medium" icon="alert" className="drawer-section" title="Late-stage activity observed">
              Detections map to command-and-control, exfiltration or impact. Check the Campaigns view for sources that progressed that far.
            </Callout>
          )}
        </>
      )}
    </Card>
  );
}

function MitreCoverage() {
  const c = useConsole();
  const [hours, setHours] = useState(24);
  const { data, error, reload } = usePoll("/api/mitre/coverage", { params: { hours }, interval: 60000 });
  const byTactic = useMemo(() => {
    const m = new Map();
    (data?.items || []).forEach((i) => {
      const k = i.tactic || "Other";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(i);
    });
    return [...m.entries()].sort((a, b) => b[1].reduce((s, x) => s + x.count, 0) - a[1].reduce((s, x) => s + x.count, 0));
  }, [data]);
  const max = Math.max(1, ...(data?.items || []).map((i) => i.count));

  return (
    <Card
      title="MITRE ATT&CK techniques"
      subtitle={data ? `${plural((data.items || []).length, "technique")} across ${plural(byTactic.length, "tactic")} · select one to hunt` : "Observed techniques"}
      icon="target"
      actions={
        <Segmented
          size="sm"
          label="MITRE window"
          value={hours}
          onChange={setHours}
          options={[
            { id: 24, label: "24 h" },
            { id: 168, label: "7 days" },
          ]}
        />
      }
    >
      {error ? (
        <ErrorState detail={error} onRetry={reload} />
      ) : !data ? (
        <SkeletonRows rows={5} />
      ) : !byTactic.length ? (
        <Empty compact icon="target">
          No techniques observed in this window.
        </Empty>
      ) : (
        <div className="col" style={{ gap: 18 }}>
          {byTactic.map(([tactic, items]) => (
            <div key={tactic}>
              <div className="row-between" style={{ marginBottom: 8 }}>
                <span className="eyebrow">{tactic}</span>
                <span className="tiny muted tnum">{num(items.reduce((s, x) => s + x.count, 0))} detections</span>
              </div>
              <div className="mitre">
                {items.map((i) => (
                  <button
                    key={`${i.technique_id}-${i.technique}`}
                    type="button"
                    className="mitre-cell is-hit"
                    style={{ "--tone": `color-mix(in srgb, var(--brand) ${Math.round(35 + (i.count / max) * 65)}%, var(--line-2))` }}
                    onClick={() => c.hunt({ mitre_id: i.technique_id, hours })}
                  >
                    <div className="mitre-id">{i.technique_id}</div>
                    <div className="mitre-name">{i.technique}</div>
                    <div className="mitre-count">
                      <span>{num(i.count)}</span>
                      <Meter value={i.count} max={max} height={4} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Threat intel feeds ──────────────────────────────────────────
function Intel() {
  const toast = useToast();
  const status = usePoll("/api/feeds/status", { interval: 60000 });
  const recent = usePoll("/api/feeds/recent", { params: { n: 40 }, interval: 60000 });
  const [refreshing, setRefreshing] = useState(false);
  const s = status.data;

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/api/feeds/refresh");
      const n = Object.values(r.data.refreshed || {}).reduce((a, b) => a + (Number(b) || 0), 0);
      toast("Feeds refreshed", "ok", { detail: `${num(n)} indicators loaded.` });
      status.reload();
      recent.reload();
    } catch (e) {
      toast("Refresh failed", "critical", { detail: e?.response?.status === 429 ? "Refreshes are rate-limited. Try again shortly." : e?.response?.data?.detail || e.message });
    } finally {
      setRefreshing(false);
    }
  };

  if (status.error) return <ErrorState detail={status.error} onRetry={status.reload} />;
  if (!s) return <Card><SkeletonRows rows={6} /></Card>;
  if (s.available === false)
    return (
      <Card>
        <Empty icon="globe" title="Threat feeds are not loaded on this server">
          {s.reason || "The feeds module is unavailable."}
        </Empty>
      </Card>
    );

  const feeds = Object.entries(s.feeds || {});
  const malware = (s.top_malware || []).map(([k, v]) => ({ key: k, label: k, value: v, color: "var(--cat-8)" }));
  const types = s.by_type || {};

  return (
    <>
      <div className="grid cols-4">
        {[
          ["All indicators", s.total_iocs, "database"],
          ["IP addresses", types.ip, "network"],
          ["Domains and URLs", (types.domain || 0) + (types.url || 0), "link"],
          ["File hashes", types.hash, "hash"],
        ].map(([l, v, i]) => (
          <Stat key={l} label={l} icon={i} tone="brand" value={num(v || 0)} sub={i === "network" && s.netblocks ? `${num(s.netblocks)} netblocks too` : "From public feeds"} />
        ))}
      </div>
      <div className="grid cols-12" style={{ marginTop: 16 }}>
        <Card
          className="span-7"
          flush
          title="Feed sources"
          subtitle="Public abuse feeds, each refreshed every 1–12 hours by the backend"
          icon="radio"
          actions={
            <Button size="sm" icon="refresh" loading={refreshing} onClick={refresh}>
              Refresh now
            </Button>
          }
        >
          {!feeds.length ? (
            <Empty compact icon="radio">
              No feed has been fetched yet.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Feed</th>
                    <th>Status</th>
                    <th className="num">Indicators</th>
                    <th className="num">Fetched</th>
                  </tr>
                </thead>
                <tbody>
                  {feeds.map(([name, f]) => (
                    <tr key={name}>
                      <td className="strong" style={{ fontWeight: 500 }}>
                        {name}
                      </td>
                      <td>
                        {f.ok ? (
                          <Badge tone="ok" dot>
                            Healthy
                          </Badge>
                        ) : (
                          <Badge tone="critical" title={f.error || ""}>
                            Failed
                          </Badge>
                        )}
                      </td>
                      <td className="num">{num(f.count)}</td>
                      <td className="num muted nowrap">{f.fetched_at ? timeAgo(f.fetched_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card className="span-5" title="Most listed malware families" subtitle="Across loaded feeds" icon="bug">
          <BarList items={malware} empty="No malware family tags in the loaded feeds." />
        </Card>
      </div>
      <Card flush title="Recently loaded indicators" subtitle="A sample of the newest entries" icon="list" style={{ marginTop: 16 }}>
        {recent.error ? (
          <ErrorState detail={recent.error} onRetry={recent.reload} />
        ) : !recent.data ? (
          <div className="card-body">
            <SkeletonRows rows={5} />
          </div>
        ) : !recent.data.items?.length ? (
          <Empty compact icon="list">
            No indicators loaded yet.
          </Empty>
        ) : (
          <div className="table-wrap table-scroll" style={{ maxHeight: 420 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Indicator</th>
                  <th>Type</th>
                  <th className="table-hide-sm">Malware</th>
                  <th>Source</th>
                  <th className="table-hide-sm num">First seen</th>
                </tr>
              </thead>
              <tbody>
                {recent.data.items.map((i, k) => (
                  <tr key={`${i.value}-${k}`}>
                    <td className="mono" style={{ fontSize: 12, maxWidth: 360, wordBreak: "break-all" }}>
                      {i.value}
                    </td>
                    <td className="muted">{i.type}</td>
                    <td className="table-hide-sm">{i.malware || <span className="muted">—</span>}</td>
                    <td className="muted">{i.source}</td>
                    <td className="table-hide-sm num muted nowrap">{i.first_seen ? fmtDateTime(i.first_seen) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────
export default function Analytics() {
  const c = useConsole();
  const tab = activeTab("analytics", c.tab);
  return (
    <>
      <PageHeader
        eyebrow="Intelligence"
        icon="barChart"
        title="Analytics"
        description="Explore detection trends, attack patterns, and threat intelligence."
      />
      <PageTabs page="analytics" />
      {tab === "trends" && <Trends />}
      {tab === "attack" && (
        <div className="stack">
          <KillChainCoverage />
          <MitreCoverage />
        </div>
      )}
      {tab === "intel" && <Intel />}
    </>
  );
}
